/**
 * Roadmap Item Like Remapper
 * Flutter: lib/data/models/roadmap_item_like.dart
 */
import type { RoadmapItemLikeDto } from '@/data/dtos/roadmapItemLike/roadmapItemLikeDto';
import type { JsonObject } from '@/data/types/json';
import type { RoadmapItemLikeEntity } from '@/domain/entities/roadmap/roadmapItemLikeEntity';
import type { RoadmapItemLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export class RoadmapItemLikeRemapper {
  static toStorage(dto: RoadmapItemLikeDto): RoadmapItemLikeStorage {
    return {
      roadmapItemId: dto.roadmapItemId,
      userId: dto.userId,
      liked: dto.liked,
      disliked: dto.disliked,
      timestamp: dto.timestamp,
      userName: dto.userName,
    };
  }

  /**
   * Maps the richer SecureStorage shape to the lean domain `likeEntity` the
   * roadmap item carries (the icon-tint source). The web equivalent of Flutter's
   * `RoadmapItemLike.toEntity()` (lib/data/models/roadmap_item_like.dart:20-25),
   * which copies only `{ roadmapItemId, liked, disliked }` — dropping the
   * persistence-only `userId`/`timestamp`/`userName`. Used by the roadmap hook's
   * `ROADMAP_LIKES` storage-prefix listener to re-tint the matching item live.
   */
  static fromStorage(storage: RoadmapItemLikeStorage): RoadmapItemLikeEntity {
    return {
      roadmapItemId: storage.roadmapItemId,
      liked: storage.liked,
      disliked: storage.disliked,
    };
  }

  static fromJson(data: JsonObject): RoadmapItemLikeDto {
    return {
      roadmapItemId: (data['roadmapItemId'] as string | undefined) ?? '',
      userId: (data['userId'] as string | undefined) ?? '',
      liked: (data['liked'] as boolean | undefined) ?? false,
      disliked: (data['disliked'] as boolean | undefined) ?? false,
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
      userName: (data['userName'] as string | undefined) ?? '',
    };
  }

  static fromJsonList(dataList: JsonObject[]): RoadmapItemLikeDto[] {
    return dataList.map(RoadmapItemLikeRemapper.fromJson);
  }
}
